import * as React from 'react';
import type { HandleThunkActionCreator } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { FieldArray, reduxForm } from 'redux-form';

import type { W_Account_FindForNotifyAll_VM } from 'ab-api/web/account/findForNotifyAll';

import type { CredentialVM } from 'ab-viewModels/company.viewModel';

import type UserGroupRequestModel from 'ab-requestModels/userGroup.requestModel';

import { COMPANY_CREDENTIAL_NOTIFICATIONS } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import type * as CompanyActions from 'af-actions/companies';

import type { CredentialNotificationFieldArrayOwnProps } from './Form';
import CredentialNotificationsForm from './Form';
import { CompanyCredentialFM } from '../formModel';
import type { CredentialFM } from '../formModel';

interface OwnProps {
	showModal: boolean;
	toggleEdit: () => void;
	credentials: Nullable<CredentialVM[]>;
	editCredentialNotifications: HandleThunkActionCreator<typeof CompanyActions.editCredentialNotificationSettings>;
	userGroups: UserGroupRequestModel[];
	users: W_Account_FindForNotifyAll_VM['rows'];
}

type Props = OwnProps & InjectedFormProps<CompanyCredentialFM, OwnProps>;

const CredentialNotificationsEdit: React.FC<Props> = (props) => {
	const { toggleEdit, editCredentialNotifications, reset, handleSubmit, showModal, invalid, submitting, initialize, credentials, userGroups, users } = props;

	React.useEffect(() => {
		if (showModal && credentials) {
			initialize(new CompanyCredentialFM(credentials));
		}
	}, [showModal, initialize, credentials]);

	const editCompanyFeatureFlags = React.useCallback(async (form: CompanyCredentialFM) => {
		await editCredentialNotifications(CompanyCredentialFM.toRM(form));
		toggleEdit();
	}, [editCredentialNotifications, toggleEdit]);

	const onClose = React.useCallback(() => {
		reset();
		toggleEdit();
	}, [reset, toggleEdit]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={showModal}
			size="xxl"
		>
			<CustomModal.Header
				closeModal={onClose}
				title="Edit Company Credential Notifications"
			/>
			<CustomModal.Body padding="none">
				<FieldArray<CredentialNotificationFieldArrayOwnProps, CredentialFM>
					component={CredentialNotificationsForm}
					name="credentials"
					userGroups={userGroups}
					users={users}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSubmit(editCompanyFeatureFlags)}
					reduxFormSubmitting={submitting}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	reduxForm({ form: COMPANY_CREDENTIAL_NOTIFICATIONS })
);

export default enhance(CredentialNotificationsEdit);
